/**
 * Utility functions for building search parameters from refine search form state
 */

/**
 * Build pair array from makes and models
 * @param {Array<string>} makes - Array of make keys (e.g., ['TOYOTA', 'HONDA'])
 * @param {Object} models - Object mapping make names to model arrays (e.g., { Toyota: ['Camry', 'Corolla'] })
 * @returns {Array<{make: string, models: Array<string>}>} Array of pair objects
 */
export function buildPairArray(makes, models) {
  const pair = [];

  if (makes.length) {
    makes.forEach((make) => {
      const modelKey = Object.keys(models)?.find((modelMake) => modelMake.toUpperCase() === make.toUpperCase());
      pair.push({
        make: make,
        models: models[modelKey] || []
      });
    });
  }

  return pair;
}

/**
 * Filter object to return only keys with true values
 * @param {Object} objValue - Object to filter
 * @returns {Array|*} Array of keys with true values, or original value if falsy
 */
function filterTrueValues(objValue) {
  if (objValue) {
    return Object.keys(objValue).filter((value) => objValue[value]);
  }
  return objValue;
}

/**
 * Build search parameters from form state
 * @param {Object} formState - Form state object containing selected filters
 * @param {Object} options - Additional options
 * @param {Object} options.value - Previous value prop to check for pair clearing
 * @returns {Object} Search parameters object
 */
export function buildSearchParams(formState, options = {}) {
  const {
    selectedMakes,
    selectedModel,
    selectedFuelType,
    selectedBodyType,
    selectedCabType,
    selectedVehicleCategory,
    selectedSeat,
    selectedPowerTrain,
    selectedTranmission,
    selectedWeeklyBudget,
    selectedPrice,
    budgetType
  } = formState;

  const makes = filterTrueValues(selectedMakes);
  const models = selectedModel;
  const fuelTypes = filterTrueValues(selectedFuelType);
  const bodyTypes = filterTrueValues(selectedBodyType);
  const cabTypes = filterTrueValues(selectedCabType);
  const vehicleCategories = filterTrueValues(selectedVehicleCategory);
  const seatTypes = filterTrueValues(selectedSeat);
  const driveTrainTypes = filterTrueValues(selectedPowerTrain);
  const tranmissionTypes = filterTrueValues(selectedTranmission);
  const weeklyBudget = selectedWeeklyBudget;
  const price = selectedPrice;

  const pair = buildPairArray(makes, models);

  // If pair was previously in URL but is now empty, explicitly set pair to empty array
  // to signal that pair should be cleared (not included from queryParams)
  const hadPairInUrl = options.value?.pair && options.value.pair.length > 0;
  const shouldClearPair = hadPairInUrl && pair.length === 0;

  const params = {
    ...(pair.length > 0 && { pair }),
    ...(shouldClearPair && { pair: [] }),
    ...(fuelTypes.length > 0 && { vehicleFueltype: fuelTypes }),
    ...(bodyTypes.length > 0 && { vehicleBodytype: bodyTypes }),
    ...(cabTypes.length > 0 && { cabType: cabTypes }),
    ...(vehicleCategories.length > 0 && { vehicleCategory: vehicleCategories }),
    ...(seatTypes.length > 0 && { vehicleSeats: seatTypes }),
    ...(price.min && { vehicleMinRRP: price.min, vehicleMaxRRP: price.max }),
    ...(driveTrainTypes.length > 0 && { vehicleDriveTrain: driveTrainTypes }),
    ...(tranmissionTypes.length > 0 && { vehicleTransmission: tranmissionTypes }),
    // Include minBudget/maxBudget when budgetType is set and a range is present (use != null so 0 is valid)
    ...(budgetType && weeklyBudget.min != null && { minBudget: weeklyBudget.min, maxBudget: weeklyBudget.max }),
    // Include budgetType whenever it's set
    ...(budgetType && { budgetType })
  };

  return params;
}
